import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core'

import { BugTraceEvent, BugTraceView } from '../../data/interfaces/bug-trace.interface'
import { WorklogService } from '../../data/services/worklog.service'

//* Icono y color por tipo de paso. Un fallo tiene que saltar a la vista sin leer: es lo primero que
//* busca quien va a arreglarlo.
const KIND_META: Record<string, { icon: string; color: string }> = {
  nav: { icon: 'fa-solid fa-signs-post', color: '#64748b' },
  click: { icon: 'fa-solid fa-hand-pointer', color: '#3B82F6' },
  http: { icon: 'fa-solid fa-arrow-right-arrow-left', color: '#94a3b8' },
  error: { icon: 'fa-solid fa-bug', color: '#d21313' },
  toast: { icon: 'fa-solid fa-comment-dots', color: '#8B5CF6' },
  session: { icon: 'fa-solid fa-user-clock', color: '#00C781' },
  custom: { icon: 'fa-solid fa-circle', color: '#94a3b8' },
}

/**
 * Los pasos que dio quien reportó el problema, en la columna derecha del detalle.
 *
 * Es el CONTENIDO de una pestaña, no una sección propia: no dibuja caja ni encabezado, porque el
 * título lo pone la pestaña que lo contiene. Tenerlos aquí fue el defecto original — usaba las
 * clases de tarjeta del detalle, que con la encapsulación de Angular no le alcanzaban, y la sección
 * salía sin caja y con el título al tamaño por defecto del navegador.
 *
 * La palabra «bitácora» no se usa en esta superficie: en este mismo modal ya es el nombre del
 * historial del trabajo. Aquí son «los pasos del reporte».
 */
@Component({
  selector: 'app-worklog-trace',
  templateUrl: './worklog-trace.component.html',
  styleUrls: ['./worklog-trace.component.scss'],
})
export class WorklogTraceComponent implements OnChanges, OnDestroy {
  private readonly worklog = inject(WorklogService)

  @Input() itemId: string | null = null
  /** Solo se pide la bitácora de los bugs: una tarea normal nunca la tiene. */
  @Input() itemType: string | null = null
  @Input() itemCode: string | null = null

  /** Cuántos pasos trajo, para que el detalle decida si pintar la pestaña y su contador. */
  @Output() loaded = new EventEmitter<number>()

  trace: BugTraceView | null = null
  loading = false
  openPayloads = new Set<number>()

  private downloadUrl: string | null = null

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['itemId'] && !changes['itemType']) return
    this.trace = null
    this.openPayloads.clear()
    //* Una tarea normal nunca tiene pasos: se avisa con cero para que la pestaña no exista.
    if (!this.itemId || this.itemType !== 'bug') {
      this.loaded.emit(0)
      return
    }

    this.loading = true
    this.worklog.getBugTrace(this.itemId).subscribe((trace) => {
      this.trace = trace
      this.loading = false
      this.loaded.emit(trace?.events?.length ?? 0)
    })
  }

  meta(kind: string) {
    return KIND_META[kind] ?? KIND_META['custom']
  }

  /** Si es el paso donde se rompió: lo que se resalta. */
  isFailure(event: BugTraceEvent): boolean {
    if (event.kind === 'error') return true
    return event.kind === 'http' && Number((event.detail as any)?.['status'] ?? 0) >= 400
  }

  /**
   * Hora relativa al momento del reporte (`-2:14`, `ahora`).
   *
   * Relativa y no absoluta porque lo que importa es la distancia al fallo: "esto pasó dos minutos
   * antes" se entiende de inmediato; "14:31:02" hay que restarlo mentalmente.
   */
  relative(event: BugTraceEvent): string {
    if (!this.trace?.context?.capturedAt) return ''
    const delta = Math.round((new Date(this.trace.context.capturedAt).getTime() - new Date(event.at).getTime()) / 1000)
    if (delta <= 1) return 'ahora'
    const m = Math.floor(delta / 60)
    const s = delta % 60
    return `-${m}:${String(s).padStart(2, '0')}`
  }

  hasPayload(event: BugTraceEvent): boolean {
    return !!event.detail && Object.keys(event.detail).length > 0
  }

  togglePayload(index: number): void {
    if (this.openPayloads.has(index)) this.openPayloads.delete(index)
    else this.openPayloads.add(index)
  }

  payloadOf(event: BugTraceEvent): string {
    return JSON.stringify(event.detail, null, 2)
  }

  /** Baja el JSON crudo. Solo responde al equipo de TI; a los demás les da 400 y se avisa. */
  download(): void {
    if (!this.itemId) return
    this.worklog.getBugTraceFile(this.itemId).subscribe((blob) => {
      if (!blob) return
      this.revoke()
      this.downloadUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = this.downloadUrl
      a.download = `bitacora-${this.itemCode ?? this.itemId}.json`
      a.click()
    })
  }

  private revoke(): void {
    if (this.downloadUrl) URL.revokeObjectURL(this.downloadUrl)
    this.downloadUrl = null
  }

  ngOnDestroy(): void {
    this.revoke()
  }
}
